import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type CSSProperties, type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icons';
import { unitWeightTotal } from '../lib/split';
import type { Unit } from '../types';

/* ---------- toast ---------- */

const ToastCtx = createContext<(msg: string) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback((m: string) => {
    setMsg(m);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(null), 2600);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <ToastCtx.Provider value={show}>
      {children}
      {msg && <div className="toast" role="status">{msg}</div>}
    </ToastCtx.Provider>
  );
}

/* ---------- sheet ---------- */

/**
 * Where sheets render: the phone frame itself, not the scrolling view they are
 * opened from. iOS Safari makes that touch-scrolling view its own stacking
 * layer, so a sheet left inside it can never rise above the floating
 * Add expense button, whatever its z-index — the button sat on top of the
 * Export sheet's Save CSV. Provided by Frame in App.tsx.
 */
export const OverlayRoot = createContext<HTMLElement | null>(null);

export function Sheet({
  title, onClose, children, footer
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const root = useContext(OverlayRoot);
  const sheet = (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
      <div className="scrim" onClick={onClose} />
      <div className="panel">
        <div className="grab" />
        <div className="phd">
          <h2>{title}</h2>
          <button className="iconbtn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        <div className="pbody">{children}</div>
        {footer && <div className="pft">{footer}</div>}
      </div>
    </div>
  );
  return root ? createPortal(sheet, root) : sheet;
}

/* ---------- household avatars ---------- */

const initial = (n: string) => (n.trim()[0] || '?').toUpperCase();

export function UnitAvatar({ unit, small }: { unit: Unit; small?: boolean }) {
  const adults = unit.people.filter((p) => !p.isChild).slice(0, 2);
  const shown = adults.length ? adults : unit.people.slice(0, 2);
  const kids = unit.people.filter((p) => p.isChild).length;
  return (
    <span className={`uav c${unit.color}${small ? ' sm' : ''}`}>
      {shown.map((p) => <i key={p.id}>{initial(p.name)}</i>)}
      {kids > 0 && <b>+{kids}</b>}
    </span>
  );
}

/** One circle standing for a whole household, where families are counted not listed. */
export function UnitDot({ unit }: { unit: Unit }) {
  const p = unit.people.find((x) => !x.isChild) || unit.people[0];
  return (
    <span className={`uav solo c${unit.color}`}>
      <i>{p ? initial(p.name) : '?'}</i>
    </span>
  );
}

export const ColorDot = ({ color }: { color: number }) => (
  <span className="dot" style={{ '--uc': `var(--u${color})` } as CSSProperties} />
);

/* ---------- small pieces ---------- */

export const Glyph = ({ name, tint }: { name: string; tint?: boolean }) => (
  <span className={`glyph${tint ? ' tint' : ''}`}><Icon name={name} /></span>
);

export const Spinner = () => <div className="spinner" aria-label="Loading" />;

export function Loading({ label }: { label?: string }) {
  return (
    <div className="center">
      <Spinner />
      {label && <div className="muted" style={{ fontSize: 13 }}>{label}</div>}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <div className="big">{title}</div>
      {children && <p>{children}</p>}
    </div>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="note">
      <Icon name="info" />
      <div>{children}</div>
    </div>
  );
}

export function Segmented<T extends string>({
  value, options, onChange
}: {
  value: T;
  options: { id: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg" role="tablist">
      {options.map((o) => (
        <button key={o.id} role="tab" aria-selected={value === o.id} onClick={() => onChange(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button className="tog" aria-pressed={on} aria-label={label} onClick={() => onChange(!on)} />
  );
}

export const useUnitLookup = (units: Unit[]) =>
  useMemo(() => {
    const byId = new Map(units.map((u) => [u.id, u]));
    return {
      unit: (id: string) => byId.get(id),
      name: (id: string) => byId.get(id)?.name ?? 'Someone',
      weight: (id: string) => {
        const u = byId.get(id);
        return u ? unitWeightTotal(u) : 0;
      }
    };
  }, [units]);

/* ---------- page chrome ---------- */

export function Page({
  title, subtitle, wordmark, left, right, children, fab, noFab
}: {
  title?: string;
  subtitle?: ReactNode;
  wordmark?: ReactNode;
  left?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  fab?: ReactNode;
  noFab?: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  return (
    <>
      <div className={`topbar${scrolled ? ' scrolled' : ''}`}>
        {left}
        <div className="tb-grow">
          {wordmark ? <div className="wordmark">{wordmark}</div> : <h1>{title}</h1>}
          {subtitle && <div className="sub">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div
        className={`view${noFab ? ' no-fab' : ''}`}
        onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 4)}
      >
        {children}
      </div>
      {fab}
    </>
  );
}

export const Section = ({ label, action }: { label: string; action?: ReactNode }) => (
  <div className="sec">
    <div className="eyebrow">{label}</div>
    {action}
  </div>
);
